import React, { useEffect, useState, useCallback } from 'react';
import { Box, Paper, Alert } from '@mui/material';
import { BaseTabs } from '../../base';
import ConfigManagementService, { ConfigScheme, SchemaPermissionConfig } from '../../../../services/ConfigManagementService';
import PageHeader from '../../base/PageHeader';

// Import subcomponents
import {
  SchemaTab,
  SchemaModal,
  ConfigurationTab,
  CollectionTab,
  CollectionItemModal,
  EditingScheme,
  EditingCollectionItem,
} from './components';
import { DEFAULT_PERMISSION } from './components/SchemaModal';

// Import hooks
import { useCollectionFilters, useLookupCache } from '../../../../hooks';

// Import helpers
import {
  getEditableCollectionProperties,
} from '../../../../utils/collectionHelpers';



/**
 * Configuration Management Page
 * 
 * Refactored main coordinator component using subcomponents:
 * - Manages global state (schemes, loading, errors)
 * - Delegates tab content to specialized components
 * - Coordinates modals and data flows
 * - Remains under 300 lines (previously 1000+)
 */
export const ConfigManagementPage: React.FC = () => {
  // ============================================
  // GLOBAL STATE
  // ============================================
  const [tabValue, setTabValue] = useState(0);
  const [schemes, setSchemes] = useState<ConfigScheme[]>([]);
  const [schemeMap, setSchemeMap] = useState<Record<string, ConfigScheme>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // ============================================
  // SCHEMA TAB STATE
  // ============================================
  const [editingScheme, setEditingScheme] = useState<EditingScheme | null>(null);
  const [schemaModalOpen, setSchemaModalOpen] = useState(false);
  const [schemaEditorValue, setSchemaEditorValue] = useState('');
  const [schemaPermission, setSchemaPermission] = useState<Omit<SchemaPermissionConfig, 'schemaKey' | 'isDefault'>>(DEFAULT_PERMISSION);

  // ============================================
  // COLLECTION TAB STATE
  // ============================================
  const [selectedCollectionKey, setSelectedCollectionKey] = useState<string | null>(null);
  const [collectionItems, setCollectionItems] = useState<any[]>([]);
  const collectionFilters = useCollectionFilters('createdAt', 'desc');
  const [collectionSchema, setCollectionSchema] = useState<Record<string, any>>({});
  
  // Inline editing
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editingRowData, setEditingRowData] = useState<Record<string, any>>({});

  // Modal editing
  const [collectionItemModalOpen, setCollectionItemModalOpen] = useState(false);
  const [isCreatingCollectionItem, setIsCreatingCollectionItem] = useState(false);
  const [editingCollectionItem, setEditingCollectionItem] = useState<EditingCollectionItem | null>(null);

  // Lookup cache
  const { cache: lookupCache, setCachedData } = useLookupCache();

  // ============================================
  // LOOKUP DATA FETCHING
  // ============================================
  /**
   * Load lookup data for all x-lookup fields in the schema
   * Populates the cache so table cells can display proper values
   */
  const loadLookupDataForSchema = async (schema: Record<string, any>, schemaKey: string) => {
    try {
      const properties = schema?.items?.properties || schema?.properties || {};
      const lookupFields = Object.entries(properties)
        .filter(([_, fieldSchema]: [string, any]) => {
          const xControl = fieldSchema['x-control'];
          return xControl?.type === 'lookup' && xControl?.settings?.source;
        })
        .map(([fieldKey, fieldSchema]: [string, any]) => ({
          fieldKey,
          config: fieldSchema['x-control'].settings,
        }));

      // Load data for each lookup field
      for (const { config } of lookupFields) {
        try {
          const response = await ConfigManagementService.searchCollectionItems(
            config.source,
            '', // Empty query to get all
            1,
            100, // Fetch more items for cache
            'createdAt',
            'desc',
            [config.renderProp] // Search in render property
          );

          // Cache all items from lookup source
          if (response.items) {
            response.items.forEach((item: any) => {
              setCachedData(config.source, item.id, item.data);
            });
          }
        } catch (err) {
          console.warn(`Failed to load lookup data for ${config.source}:`, err);
        }
      }
    } catch (err) {
      console.warn('Failed to load lookup data:', err);
    }
  };

  // ============================================
  // CONFIGURATION TAB STATE
  // ============================================
  const [selectedConfigKey, setSelectedConfigKey] = useState<string | null>(null);
  const [configData, setConfigData] = useState<Record<string, any> | null>(null);

  // ============================================
  // DATA FETCHING
  // ============================================
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const schemesData = await ConfigManagementService.getAllSchemes();
      setSchemes(schemesData);

      const map: Record<string, ConfigScheme> = {};
      schemesData.forEach((scheme) => {
        map[scheme.key] = scheme;
      });
      setSchemeMap(map);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ============================================
  // SCHEMA TAB HANDLERS
  // ============================================
  const handleOpenCreateSchema = () => {
    setEditingScheme({
      key: '',
      label: '',
      schema: {},
      description: '',
      type: 'config',
      pinToSidebar: false,
      isNew: true,
    });
    setSchemaEditorValue('{}');
    setSchemaPermission(DEFAULT_PERMISSION);
    setSchemaModalOpen(true);
  };

  const handleEditScheme = async (schemeKey: string) => {
    try {
      setLoading(true);
      const [schemeData, permData] = await Promise.all([
        ConfigManagementService.getSchemeByKey(schemeKey),
        ConfigManagementService.getPermission(schemeKey).catch(() => null),
      ]);
      setEditingScheme({
        key: schemeData.key,
        label: schemeData.label || '',
        schema: schemeData.schemaDefinition || {},
        description: schemeData.description || '',
        type: schemeData.type || 'config',
        pinToSidebar: schemeData.pinToSidebar ?? false,
        isNew: false,
      });
      setSchemaEditorValue(JSON.stringify(schemeData.schemaDefinition, null, 2));
      if (permData) {
        setSchemaPermission({
          create: permData.create,
          read: permData.read,
          update: permData.update,
          delete: permData.delete,
        });
      } else {
        setSchemaPermission(DEFAULT_PERMISSION);
      }
      setSchemaModalOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load schema');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSchema = useCallback(async (overrides?: { key?: string; label?: string; description?: string }) => {
    const effectiveKey = overrides?.key ?? editingScheme?.key ?? '';
    const effectiveLabel = overrides?.label ?? editingScheme?.label;
    const effectiveDescription = overrides?.description ?? editingScheme?.description;
    if (!editingScheme || !effectiveKey) {
      setError('Schema key is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const parsedSchema = JSON.parse(schemaEditorValue);

      await ConfigManagementService.createScheme(
        effectiveKey,
        parsedSchema,
        effectiveDescription,
        editingScheme.type,
        editingScheme.pinToSidebar ?? false,
        effectiveLabel
      );

      // Save permission config
      await ConfigManagementService.upsertPermission(effectiveKey, schemaPermission);

      setSuccess(`Schema "${effectiveKey}" saved successfully`);
      setSchemaModalOpen(false);
      setEditingScheme(null);
      await fetchData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save schema');
    } finally {
      setSaving(false);
    }
  }, [editingScheme, schemaEditorValue, schemaPermission]);

  // Memoized handlers for SchemaModal to prevent unnecessary re-renders
  const handleSchemaKeyChange = useCallback((key: string) => {
    setEditingScheme((prev) => prev ? { ...prev, key } : null);
  }, []);

  const handleSchemaLabelChange = useCallback((label: string) => {
    setEditingScheme((prev) => prev ? { ...prev, label } : null);
  }, []);

  const handleSchemaTypeChange = useCallback((type: 'config' | 'collection') => {
    setEditingScheme((prev) => prev ? { ...prev, type } : null);
  }, []);

  const handleSchemaDescriptionChange = useCallback((desc: string) => {
    setEditingScheme((prev) => prev ? { ...prev, description: desc } : null);
  }, []);

  const handleSchemaPinToSidebarChange = useCallback((pin: boolean) => {
    setEditingScheme((prev) => prev ? { ...prev, pinToSidebar: pin } : null);
  }, []);

  const handleSchemaModalClose = useCallback(() => {
    setSchemaModalOpen(false);
  }, []);

  const handleDeleteSchema = async (schemeKey: string) => {
    if (window.confirm(`Are you sure you want to delete the schema "${schemeKey}"?`)) {
      try {
        await ConfigManagementService.deleteScheme(schemeKey);
        setSuccess(`Schema "${schemeKey}" deleted successfully`);
        await fetchData();
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete schema');
      }
    }
  };

  // ============================================
  // COLLECTION TAB HANDLERS
  // ============================================
  const handleLoadCollectionItems = async (schemaKey: string, page: number = 1, search: string = '', sortBy?: string, sortOrder?: 'asc' | 'desc') => {
    try {
      setLoading(true);
      setError(null);

      const schemeDetail = await ConfigManagementService.getSchemeByKey(schemaKey);
      setSchemeMap(prev => ({
        ...prev,
        [schemaKey]: schemeDetail
      }));

      const searchFields = ConfigManagementService.getAvailableSearchFields(schemeDetail.schemaDefinition);
      const finalSortBy = sortBy || collectionFilters.sortBy;
      const finalSortOrder = sortOrder || collectionFilters.sortOrder;

      const response = await ConfigManagementService.searchCollectionItems(
        schemaKey,
        search,
        page,
        10,
        finalSortBy,
        finalSortOrder,
        searchFields
      );

      const pagination = response.pagination || {
        page: page || 1,
        limit: 10,
        total: response.items?.length || 0,
        pages: Math.ceil((response.items?.length || 0) / 10),
      };

      setCollectionItems(response.items);
      setSelectedCollectionKey(schemaKey);
      setCollectionSchema(schemeDetail.schemaDefinition?.items || schemeDetail.schemaDefinition || {});

      collectionFilters.setPaging(pagination);
      collectionFilters.setPage(page);

      if (sortBy || sortOrder) {
        collectionFilters.setSortBy(sortBy || collectionFilters.sortBy);
        collectionFilters.setSortOrder(sortOrder || collectionFilters.sortOrder);
      } else if (selectedCollectionKey !== schemaKey) {
        collectionFilters.reset();
      }

      // Load lookup data for this schema
      await loadLookupDataForSchema(
        schemeDetail.schemaDefinition?.items || schemeDetail.schemaDefinition || {},
        schemaKey
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load collection');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchCollection = async (search: string) => {
    collectionFilters.setSearch(search);
    if (selectedCollectionKey) {
      await handleLoadCollectionItems(selectedCollectionKey, 1, search);
    }
  };

  const handleSortCollection = async (columnKey: string) => {
    collectionFilters.toggleSort(columnKey);
    if (selectedCollectionKey) {
      await handleLoadCollectionItems(
        selectedCollectionKey,
        1,
        collectionFilters.search,
        columnKey === collectionFilters.sortBy ? collectionFilters.sortBy : columnKey,
        columnKey === collectionFilters.sortBy && collectionFilters.sortOrder === 'asc' ? 'desc' : 'asc'
      );
    }
  };

  const handleDeleteCollectionItem = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await ConfigManagementService.deleteCollectionItem(id, selectedCollectionKey!);
        setSuccess('Collection item deleted successfully');
        if (selectedCollectionKey) {
          await handleLoadCollectionItems(selectedCollectionKey, collectionFilters.page);
        }
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete item');
      }
    }
  };

  const handleOpenCreateCollectionItem = () => {
    if (!selectedCollectionKey) return;

    const emptyData: Record<string, any> = {};
    const editableProperties = getEditableCollectionProperties(collectionSchema);
    Object.keys(editableProperties).forEach(key => {
      emptyData[key] = '';
    });

    setIsCreatingCollectionItem(true);
    setEditingCollectionItem({
      schemaKey: selectedCollectionKey,
      data: emptyData,
      schema: collectionSchema,
    });
    setCollectionItemModalOpen(true);
  };

  const handleEditCollectionItem = (id: string) => {
    if (!selectedCollectionKey) return;

    const item = collectionItems.find((i) => i.id === id);
    if (!item) {
      setError('Collection item not found');
      return;
    }

    setIsCreatingCollectionItem(false);
    setEditingCollectionItem({
      id,
      schemaKey: selectedCollectionKey,
      data: item.data,
      schema: collectionSchema,
    });
    setCollectionItemModalOpen(true);
  };

  const handleSaveCollectionItem = async (formData: Record<string, any>) => {
    if (!editingCollectionItem || !selectedCollectionKey) return;

    try {
      setSaving(true);
      setError(null);

      if (isCreatingCollectionItem) {
        await ConfigManagementService.createCollectionItem(selectedCollectionKey, formData);
        setSuccess('Collection item created successfully');
      } else {
        const itemId = editingCollectionItem.id;
        if (!itemId) return;
        await ConfigManagementService.updateCollectionItem(itemId, selectedCollectionKey, formData);
        setSuccess('Collection item updated successfully');
      }

      setCollectionItemModalOpen(false);
      setEditingCollectionItem(null);
      setIsCreatingCollectionItem(false);

      if (selectedCollectionKey) {
        await handleLoadCollectionItems(selectedCollectionKey, collectionFilters.page);
      }

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save collection item');
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // CONFIGURATION TAB HANDLERS
  // ============================================
  const handleLoadConfigData = async (schemaKey: string) => {
    try {
      setLoading(true);
      setError(null);
      setSelectedConfigKey(schemaKey);

      const schema = await ConfigManagementService.getSchemeByKey(schemaKey);
      setSchemeMap(prev => ({
        ...prev,
        [schemaKey]: schema
      }));

      let existingData: Record<string, any> = {};

      try {
        // Use correct endpoint based on schema type
        if (schema.type === 'config') {
          // CONFIG type: use getConfig endpoint
          const configResponse = await ConfigManagementService.getConfig(schemaKey);
          existingData = configResponse.config || {};
        } else {
          // COLLECTION type: use getCollectionItems endpoint
          const collectionResponse = await ConfigManagementService.getCollectionItems(schemaKey, 1, 1);
          existingData = collectionResponse.items.length > 0 ? collectionResponse.items[0].data : {};
        }
      } catch {
        existingData = {};
      }

      // Just set the config data - no modal needed, form renders directly in ConfigurationTab
      setConfigData(existingData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load config');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfigItem = async (formData: Record<string, any>) => {
    if (!selectedConfigKey) return;

    try {
      setSaving(true);
      setError(null);

      const schema = schemeMap[selectedConfigKey];
      if (!schema) {
        setError('Schema not found');
        return;
      }

      // Use correct endpoint based on schema type
      if (schema.type === 'config') {
        await ConfigManagementService.updateConfig(selectedConfigKey, formData);
      } else {
        await ConfigManagementService.createCollectionItem(selectedConfigKey, formData);
      }
      setSuccess('Configuration saved successfully');

      // Reload the config data
      if (selectedConfigKey) {
        await handleLoadConfigData(selectedConfigKey);
      }

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // UI RENDER
  // ============================================
  const breadcrumbs = [
    { label: 'Dashboard', href: '/admin' },
    { label: 'Configuration Management', href: '/admin/configs' },
  ];

  return (
    <Box>
      <PageHeader
        title="Configuration Management"
        subtitle="Manage application configurations and JSON schemas"
        breadcrumbs={breadcrumbs}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #E6E7EB', borderRadius: '8px' }}>
        <BaseTabs
          items={[
            {
              label: 'Schemas',
              value: 0,
              content: (
                <SchemaTab
                  schemes={schemes}
                  loading={loading}
                  onCreateSchema={handleOpenCreateSchema}
                  onEditSchema={handleEditScheme}
                  onDeleteSchema={handleDeleteSchema}
                  onRefresh={fetchData}
                />
              ),
            },
            {
              label: 'Configurations',
              value: 1,
              content: (
                <ConfigurationTab
                  schemes={schemes}
                  selectedConfigKey={selectedConfigKey}
                  configData={configData}
                  loading={loading}
                  saving={saving}
                  onSelectConfig={handleLoadConfigData}
                  onSaveConfig={handleSaveConfigItem}
                  onRefresh={fetchData}
                />
              ),
            },
            {
              label: 'Collections',
              value: 2,
              content: (
                <CollectionTab
                  schemes={schemes}
                  selectedCollectionKey={selectedCollectionKey}
                  collectionItems={collectionItems}
                  collectionPaging={collectionFilters.paging}
                  collectionSearch={collectionFilters.search}
                  collectionSortBy={collectionFilters.sortBy}
                  collectionSortOrder={collectionFilters.sortOrder}
                  editingRowId={editingRowId}
                  editingRowData={editingRowData}
                  lookupCache={lookupCache}
                  loading={loading}
                  saving={saving}
                  schema={collectionSchema}
                  onSelectCollection={(key) => {
                    if (key) {
                      collectionFilters.reset();
                      handleLoadCollectionItems(key, 1);
                    } else {
                      setSelectedCollectionKey(null);
                      setCollectionItems([]);
                    }
                  }}
                  onSearch={handleSearchCollection}
                  onPageChange={(page) => {
                    if (selectedCollectionKey) {
                      handleLoadCollectionItems(selectedCollectionKey, page, collectionFilters.search);
                    }
                  }}
                  onSort={handleSortCollection}
                  onCreateItem={handleOpenCreateCollectionItem}
                  onRefresh={fetchData}
                  onStartEditRow={(item) => {
                    setEditingRowId(item.id);
                    setEditingRowData({ ...item.data });
                  }}
                  onCancelEditRow={() => {
                    setEditingRowId(null);
                    setEditingRowData({});
                  }}
                  onSaveEditRow={async () => {
                    if (!editingRowId || !selectedCollectionKey) return;
                    try {
                      setSaving(true);
                      await ConfigManagementService.updateCollectionItem(editingRowId, selectedCollectionKey, editingRowData);
                      setEditingRowId(null);
                      setEditingRowData({});
                      if (selectedCollectionKey) {
                        await handleLoadCollectionItems(selectedCollectionKey, collectionFilters.page);
                      }
                    } finally {
                      setSaving(false);
                    }
                  }}
                  onEditCellChange={(field, value) => {
                    setEditingRowData(prev => ({
                      ...prev,
                      [field]: value,
                    }));
                  }}
                  onEditCollectionItem={handleEditCollectionItem}
                  onDeleteCollectionItem={handleDeleteCollectionItem}
                />
              ),
            },
          ]}
          value={tabValue}
          onChange={(value) => setTabValue(value as number)}
          showContent
        />
      </Paper>

      {/* Modals */}
      {editingScheme && (
        <SchemaModal
          open={schemaModalOpen}
          editingScheme={editingScheme}
          schemaEditorValue={schemaEditorValue}
          saving={saving}
          permission={schemaPermission}
          onClose={handleSchemaModalClose}
          onKeyChange={handleSchemaKeyChange}
          onLabelChange={handleSchemaLabelChange}
          onTypeChange={handleSchemaTypeChange}
          onDescriptionChange={handleSchemaDescriptionChange}
          onPinToSidebarChange={handleSchemaPinToSidebarChange}
          onEditorChange={setSchemaEditorValue}
          onPermissionChange={setSchemaPermission}
          onSave={handleSaveSchema}
        />
      )}

      {editingCollectionItem && (
        <CollectionItemModal
          open={collectionItemModalOpen}
          isCreating={isCreatingCollectionItem}
          editingItem={editingCollectionItem}
          saving={saving}
          onClose={() => {
            setCollectionItemModalOpen(false);
            setIsCreatingCollectionItem(false);
          }}
          onSave={handleSaveCollectionItem}
        />
      )}


    </Box>
  );
};

export default ConfigManagementPage;
